import React, { useState, useEffect } from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Market Status ─────────────────────────────────────────────────────────────
function getMarketStatus() {
  const now = new Date();
  const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = et.getDay();
  const mins = et.getHours() * 60 + et.getMinutes();
  if (day === 0 || day === 6) return { status: 'CLOSED', label: 'Weekend',      color: '#64748b' };
  if (mins >= 570 && mins < 960)  return { status: 'OPEN',  label: 'Market Open',  color: '#00ff88' };
  if (mins >= 240 && mins < 570)  return { status: 'PRE',   label: 'Pre-Market',   color: '#ffaa00' };
  if (mins >= 960 && mins < 1200) return { status: 'AFTER', label: 'After-Hours',  color: '#ffaa00' };
  return { status: 'CLOSED', label: 'Market Closed', color: '#64748b' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const pctColor  = (v) => v >= 0 ? '#00ff88' : '#ff4444';
const fmtPct    = (v) => (v >= 0 ? '+' : '') + (v ?? 0).toFixed(2) + '%';
const fmtTime   = (iso) => { try { return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }); } catch { return iso; } };

// ── Index Card ────────────────────────────────────────────────────────────────
const IndexCard = ({ label, d }) => {
  if (!d) return null;
  const up = d.changePercent >= 0;
  return (
    <div style={{ padding: '1rem 1.25rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', minWidth: 0 }}>
      <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 700,
        letterSpacing: '0.08em', marginBottom: '0.35rem' }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#f8fafc',
        letterSpacing: '-0.02em', lineHeight: 1, marginBottom: '0.3rem' }}>
        {d.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem',
        fontSize: '0.8rem', fontWeight: 600, color: up ? '#00ff88' : '#ff4444' }}>
        {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
        {up ? '+' : ''}{d.change?.toFixed(2)} ({d.changePercent?.toFixed(2)}%)
      </div>
    </div>
  );
};

// ── Mover Row ─────────────────────────────────────────────────────────────────
const MoverRow = ({ item, onNavigate }) => {
  const up = item.changePercent >= 0;
  return (
    <div onClick={() => onNavigate && onNavigate('stock', { ticker: item.ticker })}
      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.6rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
        transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#f8fafc', minWidth: '52px' }}>{item.ticker}</div>
      <div style={{ flex: 1, fontSize: '0.75rem', color: '#64748b',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8',
        minWidth: '60px', textAlign: 'right' }}>${item.price?.toFixed(2)}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.15rem',
        fontSize: '0.8rem', fontWeight: 700, color: up ? '#00ff88' : '#ff4444',
        minWidth: '64px', justifyContent: 'flex-end' }}>
        {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {Math.abs(item.changePercent)?.toFixed(2)}%
      </div>
    </div>
  );
};

// ── Sector Bar ────────────────────────────────────────────────────────────────
const SectorBar = ({ sector, changePercent }) => {
  const up = changePercent >= 0;
  const width = Math.min(100, (Math.abs(changePercent) / 3) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.35rem 0' }}>
      <div style={{ fontSize: '0.75rem', color: '#94a3b8', minWidth: '130px', fontWeight: 500 }}>{sector}</div>
      <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: width + '%', height: '100%', borderRadius: '3px',
          background: up ? '#00ff88' : '#ff4444', transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, minWidth: '52px',
        textAlign: 'right', color: up ? '#00ff88' : '#ff4444' }}>
        {up ? '+' : ''}{changePercent?.toFixed(2)}%
      </div>
    </div>
  );
};

// ── MD1: Fear & Greed Gauge ───────────────────────────────────────────────────
const ZONE_COLOR = {
  extreme_fear: '#ef4444',
  fear:         '#f97316',
  neutral:      '#eab308',
  greed:        '#84cc16',
  extreme_greed:'#00ff88',
};

const FearGreedGauge = ({ fearGreed }) => {
  if (!fearGreed) return (
    <div style={{ textAlign: 'center', padding: '1rem', color: '#334155', fontSize: '0.78rem' }}>
      Loading…
    </div>
  );
  const { score, label, zone } = fearGreed;
  const color = ZONE_COLOR[zone] || '#64748b';

  // SVG semicircle gauge — 180deg arc
  const R = 54;
  const cx = 70, cy = 70;
  const toRad = (deg) => (deg * Math.PI) / 180;
  // Arc from 180° to 360° (left to right = 0 to 100)
  const startAngle = 180;
  const scoreAngle = startAngle + (score / 100) * 180;

  const arcPath = (start, end, r) => {
    const s = { x: cx + r * Math.cos(toRad(start)), y: cy + r * Math.sin(toRad(start)) };
    const e = { x: cx + r * Math.cos(toRad(end)),   y: cy + r * Math.sin(toRad(end))   };
    const large = end - start > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const needleAngle = scoreAngle;
  const needleLen = R - 8;
  const nx = cx + needleLen * Math.cos(toRad(needleAngle));
  const ny = cy + needleLen * Math.sin(toRad(needleAngle));

  // Zone colour stops across the arc
  const zones = [
    { end: 20,  color: '#ef4444' },
    { end: 40,  color: '#f97316' },
    { end: 60,  color: '#eab308' },
    { end: 80,  color: '#84cc16' },
    { end: 100, color: '#00ff88' },
  ];

  return (
    <div style={{ textAlign: 'center' }}>
      <svg viewBox="0 0 140 80" style={{ width: '100%', maxWidth: 180, display: 'block', margin: '0 auto' }}>
        {/* Background track */}
        <path d={arcPath(180, 360, R)} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="10" strokeLinecap="round" />
        {/* Coloured zone arcs */}
        {zones.map((z, i) => {
          const prevEnd = i === 0 ? 0 : zones[i - 1].end;
          const sa = 180 + (prevEnd / 100) * 180;
          const ea = 180 + (z.end / 100) * 180;
          return (
            <path key={i} d={arcPath(sa, ea, R)} fill="none"
              stroke={z.color} strokeWidth="10" opacity="0.25" strokeLinecap="butt" />
          );
        })}
        {/* Active arc up to score */}
        <path d={arcPath(180, scoreAngle, R)} fill="none"
          stroke={color} strokeWidth="10" strokeLinecap="round" opacity="0.9" />
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny}
          stroke="#f8fafc" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
        <circle cx={cx} cy={cy} r="4" fill="#f8fafc" opacity="0.9" />
        {/* Score number */}
        <text x={cx} y={cy - 18} textAnchor="middle"
          style={{ fontSize: '18px', fontWeight: 900, fill: color, fontFamily: 'inherit' }}>
          {score}
        </text>
      </svg>
      <div style={{ fontSize: '0.82rem', fontWeight: 800, color, marginTop: '-4px',
        letterSpacing: '0.04em' }}>{label.toUpperCase()}</div>
    </div>
  );
};

// ── MD2: VIX Card ─────────────────────────────────────────────────────────────
const VixCard = ({ vix }) => {
  if (!vix) return null;
  const labelColor = vix.label === 'Low' ? '#00ff88' : vix.label === 'Normal' ? '#64748b' : vix.label === 'Elevated' ? '#ffaa00' : '#ef4444';
  return (
    <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
      <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700,
        letterSpacing: '0.08em', marginBottom: '0.3rem' }}>VIX (VOLATILITY)</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.4rem', fontWeight: 900, color: '#f8fafc' }}>{vix.price}</span>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: pctColor(vix.changePercent) }}>
          {fmtPct(vix.changePercent)}
        </span>
      </div>
      {vix.asOf && vix.marketState !== 'LIVE' && (
        <div style={{ fontSize: '0.6rem', color: '#475569', marginBottom: '0.3rem' }}>📅 {vix.asOf}</div>
      )}
      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.55rem',
        borderRadius: '20px', background: labelColor + '18',
        border: '1px solid ' + labelColor + '35', color: labelColor }}>
        {vix.label}
      </span>
    </div>
  );
};

// ── MD3: Yields Strip ─────────────────────────────────────────────────────────
const YieldsStrip = ({ yields }) => {
  if (!yields) return null;
  const tenors = [
    { label: '1Y', data: yields.oneYear },
    { label: '5Y', data: yields.fiveYear },
    { label: '10Y', data: yields.tenYear },
    { label: '30Y', data: yields.thirtyYear },
  ].filter(t => t.data);
  return (
    <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
      <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700,
        letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
        TREASURY YIELDS {yields.inverted && (
          <span style={{ color: '#ef4444', marginLeft: '0.4rem' }}>⚠ INVERTED</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {tenors.map(({ label, data }) => (
          <div key={label}>
            <div style={{ fontSize: '0.62rem', color: '#475569', marginBottom: '0.15rem' }}>{label}</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f8fafc' }}>${data.price}</div>
            <div style={{ fontSize: '0.68rem', fontWeight: 600, color: pctColor(data.changePercent) }}>
              {fmtPct(data.changePercent)}
            </div>
          </div>
        ))}
        {yields.spread10v2 != null && (
          <div>
            <div style={{ fontSize: '0.62rem', color: '#475569', marginBottom: '0.15rem' }}>10Y-2Y</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700,
              color: yields.inverted ? '#ef4444' : '#f8fafc' }}>
              {yields.spread10v2 > 0 ? '+' : ''}{yields.spread10v2}
            </div>
            <div style={{ fontSize: '0.62rem', color: yields.inverted ? '#ef4444' : '#64748b' }}>
              {yields.inverted ? 'INVERTED' : 'SPREAD'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── MD4: Commodities Strip ────────────────────────────────────────────────────
const CommoditiesStrip = ({ commodities }) => {
  if (!commodities) return null;
  const items = [
    commodities.oil    ? { label: `Oil`,    sub: 'USO',  data: commodities.oil    } : null,
    commodities.gold   ? { label: `Gold`,   sub: 'GLD',  data: commodities.gold   } : null,
    commodities.silver ? { label: `Silver`, sub: 'SLV',  data: commodities.silver } : null,
    commodities.copper ? { label: `Copper`, sub: 'CPER', data: commodities.copper } : null,
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
      <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700,
        letterSpacing: '0.08em', marginBottom: '0.5rem' }}>COMMODITIES</div>
      <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap' }}>
        {items.map(({ label, sub, data }) => (
          <div key={label}>
            <div style={{ fontSize: '0.62rem', color: '#475569', marginBottom: '0.15rem' }}>
              {label} <span style={{ color: '#334155' }}>({sub})</span>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f8fafc' }}>${data.price}</div>
            <div style={{ fontSize: '0.68rem', fontWeight: 600, color: pctColor(data.changePercent) }}>
              {fmtPct(data.changePercent)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── MD5: Upcoming Earnings ────────────────────────────────────────────────────
const EarningsWidget = ({ earnings, isLoggedIn, onSignIn, onRequestInvite }) => {
  const VISIBLE = 3;
  const shown   = earnings.slice(0, VISIBLE);
  const locked  = earnings.slice(VISIBLE);
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px',
      padding: '1rem 1.25rem' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b',
        letterSpacing: '0.08em', marginBottom: '0.75rem' }}>📅 EARNINGS THIS WEEK</div>
      {earnings.length === 0
        ? <div style={{ fontSize: '0.78rem', color: '#334155' }}>No major S&P 500 earnings this week.</div>
        : (
          <>
            {shown.map((e, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '0.45rem 0',
                borderBottom: i < shown.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <div>
                  <span style={{ fontWeight: 800, color: '#00d4ff', fontSize: '0.85rem',
                    marginRight: '0.5rem' }}>{e.symbol}</span>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{e.name}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.7rem', color: '#475569' }}>{e.date}</div>
                  {e.hour && (
                    <div style={{ fontSize: '0.65rem', color: '#334155' }}>
                      {e.hour === 'BMO' ? '🌅 Pre-mkt' : '🌆 After-hrs'}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {locked.length > 0 && (
              !isLoggedIn ? (
                <div style={{ marginTop: '0.75rem', padding: '0.65rem 0.85rem',
                  background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', color: '#475569', marginBottom: '0.5rem' }}>
                    🔒 +{locked.length} more — members only
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                    <button onClick={onSignIn}
                      style={{ padding: '0.35rem 0.85rem',
                        background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                        border: 'none', borderRadius: '6px', color: '#050d1a',
                        fontWeight: 800, cursor: 'pointer', fontSize: '0.73rem',
                        fontFamily: 'inherit' }}>Sign In</button>
                    <button onClick={onRequestInvite}
                      style={{ padding: '0.35rem 0.85rem', background: 'transparent',
                        border: '1px solid rgba(0,255,136,0.3)', borderRadius: '6px',
                        color: '#00ff88', fontWeight: 700, cursor: 'pointer',
                        fontSize: '0.73rem', fontFamily: 'inherit' }}>Request Invite</button>
                  </div>
                </div>
              ) : (
                locked.map((e, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '0.45rem 0',
                    borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    <div>
                      <span style={{ fontWeight: 800, color: '#00d4ff', fontSize: '0.85rem',
                        marginRight: '0.5rem' }}>{e.symbol}</span>
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{e.name}</span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#475569' }}>{e.date}</div>
                  </div>
                ))
              )
            )}
          </>
        )}
    </div>
  );
};

// ── MD6: Economic Events Today ────────────────────────────────────────────────
const IMPACT_COLOR = { high: '#ef4444', medium: '#ffaa00' };
const EconomicWidget = ({ events }) => (
  <div style={{ background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px',
    padding: '1rem 1.25rem' }}>
    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b',
      letterSpacing: '0.08em', marginBottom: '0.75rem' }}>🏛 ECONOMIC EVENTS TODAY</div>
    {events.length === 0
      ? <div style={{ fontSize: '0.78rem', color: '#334155' }}>No high/medium impact events today.</div>
      : events.map((e, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
          padding: '0.45rem 0',
          borderBottom: i < events.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.4rem',
            borderRadius: '4px', background: (IMPACT_COLOR[e.impact] || '#64748b') + '18',
            color: IMPACT_COLOR[e.impact] || '#64748b', whiteSpace: 'nowrap', marginTop: '1px' }}>
            {e.impact?.toUpperCase()}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.78rem', color: '#f8fafc', fontWeight: 500 }}>{e.event}</div>
            <div style={{ fontSize: '0.68rem', color: '#475569', marginTop: '0.1rem' }}>
              {fmtTime(e.time)}
              {e.estimate != null && ` · Est: ${e.estimate}`}
              {e.actual   != null && ` · Act: ${e.actual}`}
            </div>
          </div>
        </div>
      ))}
  </div>
);

// ── MD7: AI Market Brief ──────────────────────────────────────────────────────
const AIBrief = ({ brief, isLoggedIn, onSignIn, onRequestInvite }) => {
  if (!brief) return null;

  // Split into sentences for guest truncation
  const sentences = brief.match(/[^.!?]+[.!?]+/g) || [brief];
  const firstSentence = sentences[0]?.trim() || brief;

  return (
    <div style={{ padding: '1.25rem', background: 'rgba(0,212,255,0.04)',
      border: '1px solid rgba(0,212,255,0.12)', borderRadius: '14px',
      marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#00d4ff',
        letterSpacing: '0.1em', marginBottom: '0.75rem' }}>
        🤖 AI MARKET BRIEF
        <span style={{ marginLeft: '0.5rem', fontSize: '0.62rem', color: '#334155',
          fontWeight: 500 }}>Generated daily at market open · Not investment advice</span>
      </div>
      {isLoggedIn ? (
        <p style={{ margin: 0, color: '#cbd5e1', lineHeight: 1.7, fontSize: '0.88rem' }}>
          {brief}
        </p>
      ) : (
        <>
          <p style={{ margin: '0 0 0.75rem', color: '#cbd5e1', lineHeight: 1.7,
            fontSize: '0.88rem' }}>
            {firstSentence}
          </p>
          <div style={{ padding: '0.65rem 1rem', background: 'rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#475569' }}>
              🔒 Full brief — members only
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={onSignIn}
                style={{ padding: '0.35rem 0.85rem',
                  background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
                  border: 'none', borderRadius: '6px', color: '#050d1a',
                  fontWeight: 800, cursor: 'pointer', fontSize: '0.73rem',
                  fontFamily: 'inherit' }}>Sign In</button>
              <button onClick={onRequestInvite}
                style={{ padding: '0.35rem 0.85rem', background: 'transparent',
                  border: '1px solid rgba(0,255,136,0.3)', borderRadius: '6px',
                  color: '#00ff88', fontWeight: 700, cursor: 'pointer',
                  fontSize: '0.73rem', fontFamily: 'inherit' }}>Request Invite</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── MD8: Breadth Signal ───────────────────────────────────────────────────────
const BreadthWidget = ({ breadth }) => {
  if (!breadth) return null;
  const signalColor = breadth.signal === 'BULLISH' ? '#00ff88' : breadth.signal === 'BEARISH' ? '#ff4444' : '#64748b';
  const barWidth = breadth.ratio ?? 50;
  return (
    <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
      <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700,
        letterSpacing: '0.08em', marginBottom: '0.5rem' }}>MARKET BREADTH</div>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '0.75rem', color: '#00ff88', fontWeight: 600 }}>
          ▲ {breadth.gainers} up
        </span>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: signalColor,
          padding: '0.1rem 0.5rem', background: signalColor + '15',
          border: '1px solid ' + signalColor + '30', borderRadius: '20px' }}>
          {breadth.signal}
        </span>
        <span style={{ fontSize: '0.75rem', color: '#ff4444', fontWeight: 600 }}>
          ▼ {breadth.losers} down
        </span>
      </div>
      <div style={{ height: '5px', background: 'rgba(255,68,68,0.3)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: barWidth + '%', height: '100%', background: '#00ff88',
          borderRadius: '3px', transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
};


// ── Macro Panel (DXY + Credit Spreads + Ratios) ───────────────────────────
const SignalPill = ({ signal }) => {
  const colors = {
    RISK_OFF: '#ef4444', STRESS: '#ef4444', RECESSION_RISK: '#ef4444',
    RISK_ON: '#00ff88', GROWTH: '#00ff88', STRONG: '#00ff88',
    NEUTRAL: '#64748b', WEAK: '#ffaa00',
  };
  const color = colors[signal] || '#64748b';
  return (
    <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '0.1rem 0.45rem',
      borderRadius: '20px', background: color + '15',
      border: '1px solid ' + color + '30', color }}>
      {signal}
    </span>
  );
};

const MacroPanel = ({ macro }) => {
  if (!macro) return null;
  return (
    <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px',
      marginBottom: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700,
        letterSpacing: '0.08em', marginBottom: '0.65rem' }}>🌍 MACRO SIGNALS</div>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        {/* DXY */}
        {macro.dxy && (
          <div>
            <div style={{ fontSize: '0.62rem', color: '#475569', marginBottom: '0.2rem' }}>
              USD INDEX (UUP)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f8fafc' }}>
                ${macro.dxy.price}
              </span>
              <span style={{ fontSize: '0.72rem', fontWeight: 600,
                color: macro.dxy.changePercent >= 0 ? '#00ff88' : '#ff4444' }}>
                {fmtPct(macro.dxy.changePercent)}
              </span>
              <SignalPill signal={macro.dxy.signal} />
            </div>
          </div>
        )}
        {/* Credit Spreads */}
        {macro.creditSpreads && (
          <div>
            <div style={{ fontSize: '0.62rem', color: '#475569', marginBottom: '0.2rem' }}>
              CREDIT SPREADS (HYG/LQD)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                HYG {fmtPct(macro.creditSpreads.hyg.changePercent)} /
                LQD {fmtPct(macro.creditSpreads.lqd.changePercent)}
              </span>
              <SignalPill signal={macro.creditSpreads.signal} />
            </div>
          </div>
        )}
        {/* Gold/Silver Ratio */}
        {macro.goldSilverRatio && (
          <div>
            <div style={{ fontSize: '0.62rem', color: '#475569', marginBottom: '0.2rem' }}>
              GOLD/SILVER RATIO
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f8fafc' }}>
                {macro.goldSilverRatio.value}
              </span>
              <SignalPill signal={macro.goldSilverRatio.signal} />
            </div>
            <div style={{ fontSize: '0.6rem', color: '#334155', marginTop: '0.1rem' }}>
              {macro.goldSilverRatio.interpretation}
            </div>
          </div>
        )}
        {/* Copper/Gold Ratio */}
        {macro.copperGoldRatio && (
          <div>
            <div style={{ fontSize: '0.62rem', color: '#475569', marginBottom: '0.2rem' }}>
              COPPER/GOLD RATIO
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f8fafc' }}>
                {macro.copperGoldRatio.value}
              </span>
              <SignalPill signal={macro.copperGoldRatio.signal} />
            </div>
            <div style={{ fontSize: '0.6rem', color: '#334155', marginTop: '0.1rem' }}>
              {macro.copperGoldRatio.interpretation}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function MarketDashboard({
  marketData, topGainers, topLosers, mostActive, onNavigate,
  isLoggedIn, onSignIn, onRequestInvite,
}) {
  const [tab,          setTab]          = useState('gainers');
  const [sectors,      setSectors]      = useState([]);
  const [intelligence, setIntelligence] = useState(null);
  const [now, setNow] = useState(new Date()); // live clock — refreshes every 30s
  const marketStatus = getMarketStatus();

  // Refresh clock every 30s — no API call, purely local Intl computation
  // `now` is used directly in the market status bar JSX below (ET time + local time)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetch(API_URL + '/api/market/sectors')
      .then(r => r.json())
      .then(d => { if (d.success && d.data?.length) setSectors(d.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(API_URL + '/api/market/intelligence')
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setIntelligence(d.data); })
      .catch(() => {});
  }, []);

  const moversMap    = { gainers: topGainers, losers: topLosers, active: mostActive };
  const currentMovers = (moversMap[tab] || []).slice(0, 8);
  const intel         = intelligence;

  return (
    <div style={{ marginBottom: '2rem' }}>

      {/* ── Market Status Bar — live clock, viewer local time ── */}
      {(() => {
        // `now` state drives re-render every 30s via setInterval above
        const etOpts = { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true };
        const etTime = now.toLocaleTimeString('en-US', etOpts);   // e.g. "09:30 AM"

        // Viewer's local time — browser detects timezone automatically
        const localTZ    = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const localOpts  = { hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short' };
        const localTime  = now.toLocaleTimeString('en-US', localOpts); // e.g. "6:30 AM PDT"
        const isLocalET  = localTZ === 'America/New_York' || localTZ.startsWith('America/New_Y');

        // Next market event text
        const et    = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        const day   = et.getDay();
        const mins  = et.getHours() * 60 + et.getMinutes();
        let nextEvent = '';
        if (marketStatus.status === 'OPEN') {
          const minsLeft = 960 - mins;
          nextEvent = `Closes in ${Math.floor(minsLeft / 60)}h ${minsLeft % 60}m`;
        } else if (marketStatus.status === 'PRE') {
          const minsLeft = 570 - mins;
          nextEvent = `Opens in ${Math.floor(minsLeft / 60)}h ${minsLeft % 60}m`;
        } else if (day >= 1 && day <= 4 && mins >= 960) {
          // After hours on Mon-Thu — opens tomorrow
          nextEvent = 'Opens tomorrow 9:30 AM ET';
        } else if (day === 5 && mins >= 960) {
          nextEvent = 'Opens Monday 9:30 AM ET';
        } else if (day === 6) {
          nextEvent = 'Opens Monday 9:30 AM ET';
        } else if (day === 0) {
          nextEvent = 'Opens Monday 9:30 AM ET';
        } else {
          nextEvent = 'Opens 9:30 AM ET';
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem',
            marginBottom: '1rem', flexWrap: 'wrap',
            padding: '0.5rem 0.75rem', borderRadius: 10,
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>

            {/* Status pill */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.25rem 0.7rem', borderRadius: '20px',
              background: marketStatus.color + '18', border: '1px solid ' + marketStatus.color + '40' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: marketStatus.color,
                boxShadow: marketStatus.status === 'OPEN' ? '0 0 5px ' + marketStatus.color : 'none',
                display: 'inline-block',
                animation: marketStatus.status === 'OPEN' ? 'pulse 2s infinite' : 'none' }} />
              <span style={{ fontSize: '0.7rem', fontWeight: 700,
                color: marketStatus.color, letterSpacing: '0.05em' }}>
                {marketStatus.label.toUpperCase()}
              </span>
            </div>

            {/* ET time */}
            <span style={{ fontSize: '0.72rem', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
              {etTime} <span style={{ color: '#334155' }}>ET</span>
            </span>

            {/* Separator */}
            <span style={{ color: '#334155', fontSize: '0.72rem' }}>·</span>

            {/* Viewer local time — only show if different from ET */}
            {!isLocalET && (
              <>
                <span style={{ fontSize: '0.72rem', color: '#475569' }}>
                  Your time: <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{localTime}</span>
                </span>
                <span style={{ color: '#334155', fontSize: '0.72rem' }}>·</span>
              </>
            )}

            {/* Next event */}
            <span style={{ fontSize: '0.72rem', color: '#475569' }}>{nextEvent}</span>

          </div>
        );
      })()}

      {/* ── MD7: AI Brief (top of page — highest value) ── */}
      {intel?.aiBrief && (
        <AIBrief
          brief={intel.aiBrief}
          isLoggedIn={isLoggedIn}
          onSignIn={onSignIn}
          onRequestInvite={onRequestInvite}
        />
      )}

      {/* ── Indices Grid ── */}
      {marketData?.indices && (
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem', marginBottom: '1.25rem' }}>
          <IndexCard label="S&P 500"      d={marketData.indices.sp500} />
          <IndexCard label="DOW"          d={marketData.indices.dow} />
          <IndexCard label="NASDAQ"       d={marketData.indices.nasdaq} />
          <IndexCard label="RUSSELL 2000" d={marketData.indices.russell_2000} />
        </div>
      )}

      {/* ── Intelligence Row: Fear & Greed + VIX + Yields + Commodities ── */}
      {intel && (
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '0.75rem', marginBottom: '1.25rem', alignItems: 'start' }}>

          {/* MD1: Fear & Greed */}
          <div style={{ background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px',
            padding: '1rem 0.75rem' }}>
            <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700,
              letterSpacing: '0.08em', marginBottom: '0.5rem', textAlign: 'center' }}>
              FEAR & GREED
            </div>
            <FearGreedGauge fearGreed={intel.fearGreed} />
          </div>

          {/* MD2: VIX */}
          <VixCard vix={intel.vix} />

          {/* MD3: Yields */}
          <YieldsStrip yields={intel.yields} />

          {/* MD4: Commodities */}
          <CommoditiesStrip commodities={intel.commodities} />
        </div>
      )}

      {/* ── MD8: Breadth ── */}
      {intel?.breadth && (
        <div style={{ marginBottom: '1.25rem' }}>
          <BreadthWidget breadth={intel.breadth} />
        </div>
      )}

      {/* ── Macro Panel ── */}
      {intel?.macro && <MacroPanel macro={intel.macro} />}

      {/* ── Movers + Sectors ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: '1rem', alignItems: 'start', marginBottom: '1.25rem' }}>

        {/* Movers Panel */}
        <div style={{ background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px',
          padding: '1rem 1.25rem' }}>
          <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.75rem' }}>
            {[
              { key: 'gainers', label: '▲ Gainers', color: '#00ff88' },
              { key: 'losers',  label: '▼ Losers',  color: '#ff4444' },
              { key: 'active',  label: '⚡ Active',  color: '#ffaa00' },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{ padding: '0.3rem 0.75rem', borderRadius: '20px', border: 'none',
                  cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
                  fontFamily: 'inherit', transition: 'all 0.15s',
                  background: tab === t.key ? t.color + '18' : 'transparent',
                  color: tab === t.key ? t.color : '#475569',
                  outline: tab === t.key ? '1px solid ' + t.color + '35' : '1px solid transparent' }}>
                {t.label}
              </button>
            ))}
          </div>
          {currentMovers.length > 0
            ? currentMovers.map((item, i) => (
                <MoverRow key={i} item={item} onNavigate={onNavigate} />
              ))
            : <div style={{ padding: '1rem 0', textAlign: 'center',
                color: '#334155', fontSize: '0.8rem' }}>Loading market data…</div>
          }
        </div>

        {/* Sector Heatmap */}
        <div style={{ background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px',
          padding: '1rem 1.25rem' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b',
            letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
            📊 SECTOR PERFORMANCE
          </div>
          {sectors.length > 0
            ? sectors.map((s, i) => (
                <SectorBar key={i} sector={s.sector} changePercent={s.changePercent} />
              ))
            : <div style={{ padding: '1rem 0', textAlign: 'center',
                color: '#334155', fontSize: '0.8rem' }}>Loading sectors…</div>
          }
        </div>
      </div>

      {/* ── MD5 + MD6: Earnings + Economic ── */}
      {intel && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '1rem', alignItems: 'start' }}>
          <EarningsWidget
            earnings={intel.earningsToday || []}
            isLoggedIn={isLoggedIn}
            onSignIn={onSignIn}
            onRequestInvite={onRequestInvite}
          />
          <EconomicWidget events={intel.economicToday || []} />
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
